import {memo, useCallback, useMemo, useState} from 'react';

import styled, {css} from 'styled-components';

import {flexCenterVertically} from '~/shared/theme/FlexLayout';
import ClickOutsideDiv, {ClickOutsideDivProps} from '~/shared/components/ClickOutsideDiv';
import {flipOnLTR} from '~/shared/theme/utils';
import {SupportedLanguages, DEFAULT_CULTURE, DEFAULT_LANGUAGE_KEY, EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {setCurrentLanguageCookie} from '~/shared/utils/cookies';
import {changeLanguagePrefixToUrlOrPath, getLocalizationService} from '~/shared/services/localisationService';
import {Link} from '~/shared/router';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {PIE_COUNTRY_ICONS} from '~/shared/consts/header';
import {usePreventBodyScrolling} from '~/shared/hooks';
import CheckedIcon from '~/assets/images/icons/checked-icon.svg?react';
import PieButton from '~/shared/theme/pie/PieButton';

type LanguageSelectorVariant = 'ghost' | 'normal';

// #region styled components
const Root = styled.div`
  display: flex;
`;

const ChangeLangButton = styled(PieButton).attrs({
  variant: 'ghostLarge',
  iconButton: true,
})<{$variant: LanguageSelectorVariant}>`
  direction: ltr;
  ${flexCenterVertically}
  align-self: center;
  ${({$variant, theme}) => (
    $variant === 'normal'
      ? css`
          height: 32px;
          width: 32px;
          background-color: ${theme.colors.actionSecondary};
          padding: 5px;
          &:hover {
            background-color: ${theme.colors.actionSecondaryHover};
          }
        `
      : css`
          padding: 10px;
        `
  )}
`;

const CircleFlag = styled(ImageWithAlt)<{isOpen?: boolean}>`
  cursor: pointer;
  border-radius: 50%;
  ${({theme, isOpen}) => `
    background-color: ${isOpen ? theme.colors.secondaryActive : theme.colors.gray920};
    border-color: ${isOpen ? theme.colors.secondaryActive : theme.colors.gray920};
  `}
  padding: 1px;
  width: 24px;
  height: 24px;
  background-color: transparent;
`;

const ClickOutsideWithoutExtraProps = ({isOpen: _ignore, ...rest}: ClickOutsideDivProps & {
  isOpen: boolean;
}) => <ClickOutsideDiv {...rest} />;

const Dropdown = styled(ClickOutsideWithoutExtraProps).attrs({
  tabIndex: -1,
})<{isOpen: boolean}>`
  display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
  width: 200px;
  position: absolute;
  top: 35px;
  ${flipOnLTR`
    left: 0px;
  `}
  padding: 8px;
  box-shadow: ${({theme}) => theme.shadows.shadow2};
  background: ${({theme}) => theme.colors.surface};
  border-radius: 12px;
  color: ${({theme}) => theme.colors.gray000};
  z-index: ${({theme}) => theme.zIndex.languageChangeButton};
`;

const languageOptionAttrs = () => ({role: 'link'});

const LanguageOption = styled(Link).attrs(languageOptionAttrs)`
  ${flexCenterVertically}
  height: 56px;
  cursor: pointer;
  padding: 16px;
  gap: 16px;
  border-radius: 8px;

  &:hover {
    background: ${({theme}) => theme.colors.gray910};

    img {
      border-color: ${({theme}) => theme.colors.secondaryHover};
      background-color: ${({theme}) => theme.colors.secondaryHover};
    }
  }
`;

const OptionText = styled.div<{bold?: boolean}>`
  flex-grow: 1;
  ${({bold}) => bold && 'font-weight: bold;'}
`;

const StyledCheckedIcon = styled(CheckedIcon)`
  transform: scale(1.2);
`;
// #endregion

export const changeLanguage = (languageKey = DEFAULT_LANGUAGE_KEY) => {
  const culture =
    SupportedLanguages.find(({key: supportedLanguageKey}) => supportedLanguageKey === languageKey)?.culture ||
    DEFAULT_CULTURE;

  setCurrentLanguageCookie({culture, languageKey});
  // eslint-disable-next-line no-restricted-properties
  const {pathname, search} = window.location || '';
  // eslint-disable-next-line no-restricted-properties
  let fullPathname = pathname;
  if (search) {
    fullPathname += search;
  }
  // eslint-disable-next-line no-restricted-properties
  const newUrl = changeLanguagePrefixToUrlOrPath(fullPathname, languageKey);
  // eslint-disable-next-line no-restricted-properties
  window.location.replace(newUrl);
};

const LanguageSelector = ({className = '', variant = 'ghost'}: {className?: string; variant?: LanguageSelectorVariant}) => {
  const {t, currentLanguageKey} = getLocalizationService();
  const [isOpen, setIsOpen] = useState(false);
  usePreventBodyScrolling({shouldPreventScroll: isOpen});
  const currentLanguageObj = useMemo(
    () => SupportedLanguages.find(({key}) => key === currentLanguageKey) || EMPTY_OBJECT,
    [currentLanguageKey],
  );

  const onDropdownClickOutside = useCallback((e: Event) => {
    if (!isOpen) {
      return;
    }

    e.stopPropagation();
    setIsOpen(false);
  }, [isOpen]);

  const handleLanguageSelection = useCallback(
    (languageKey = DEFAULT_LANGUAGE_KEY) => {
      if (currentLanguageObj.key === languageKey) {
        setIsOpen(false);
        return;
      }
      setIsOpen(false);

      changeLanguage(languageKey);
    },
    [currentLanguageObj.key],
  );

  return (
    <Root className={className}>
      <ChangeLangButton
        $variant={variant}
        isOpen={isOpen}
        onClick={() => setIsOpen(s => !s)}
        isExpandable
        aria-controls="header-language-dropdown"
        aria-label={t('change_language')}
        id="language-selector-button"
      >
        <CircleFlag
          src={PIE_COUNTRY_ICONS[currentLanguageObj.icon]}
          alt={currentLanguageObj.name}
          isOpen={isOpen}
        />
      </ChangeLangButton>

      <Dropdown isOpen={isOpen} onClickOutside={onDropdownClickOutside} id="header-language-dropdown">
        {SupportedLanguages.map(({key, name, icon}) => (
          <LanguageOption key={key} onClick={() => handleLanguageSelection(key)}>
            <CircleFlag
              src={PIE_COUNTRY_ICONS[icon]}
              noAlt
            />
            <OptionText bold={currentLanguageKey === key}>{name}</OptionText>
            {currentLanguageKey === key && <StyledCheckedIcon />}
          </LanguageOption>
        ))}
      </Dropdown>
    </Root>
  );
};

export default memo(LanguageSelector);
